import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  BedDouble,
  Coffee,
  Sparkles,
  Check,
  ArrowRight,
  Activity,
  ChevronRight,
} from "lucide-react";
import {
  parseFixSlug,
  lifestyleIndex,
  indexBand,
  scorecardMessage,
  buildFaq,
  SYMPTOMS,
  OCCUPATIONS,
  toolPath,
} from "@/lib/seoData";
import { setPageMeta, SITE_URL } from "@/lib/pageMeta";
import SmartwatchCTA from "@/components/silentscan/SmartwatchCTA";
import ComplianceFooter from "@/components/silentscan/ComplianceFooter";
import { useChatbot } from "@/context/ChatbotContext";

export default function ProgrammaticTool() {
  const { slug } = useParams();
  const parsed = useMemo(() => parseFixSlug(slug), [slug]);
  const { openChatbot } = useChatbot();
  const [sleep, setSleep] = useState(6);
  const [caffeine, setCaffeine] = useState(2);

  const index = lifestyleIndex(sleep, caffeine);
  const band = indexBand(index);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!parsed) return;
    const { symptom, occupation } = parsed;
    setPageMeta({
      title: `Free AI Screening: How to Fix ${symptom.name} for ${occupation.name} | SilentScan AI`,
      description: `Free interactive lifestyle screening for ${occupation.name.toLowerCase()} dealing with ${symptom.name.toLowerCase()}. Instant lifestyle index, gentle non-clinical suggestions, no login required.`,
    });
  }, [parsed]);

  if (!parsed) return <Navigate to="/tools" replace />;
  const { symptom, occupation } = parsed;

  const relatedSymptoms = OCCUPATIONS.filter((o) => o.slug !== occupation.slug).slice(0, 4);
  const relatedOccupations = SYMPTOMS.filter((s) => s.slug !== symptom.slug).slice(0, 4);

  return (
    <div data-testid="programmatic-tool-page" className="min-h-screen bg-[#05050A] text-white">
      <div className="max-w-4xl mx-auto px-6 pt-24 pb-16">
        <Link
          to="/tools"
          data-testid="ptool-back"
          className="inline-flex items-center gap-2 text-xs text-zinc-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          All free wellness tools
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8"
        >
          <div className="text-[11px] uppercase tracking-[0.3em] text-cyan-400 font-semibold">
            Free AI Screening · {occupation.name}
          </div>
          <h1
            data-testid="ptool-heading"
            className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-tight"
          >
            How to fix {symptom.name.toLowerCase()}{" "}
            <span className="text-zinc-500">for {occupation.name.toLowerCase()}</span>
          </h1>
          <p className="mt-4 text-sm sm:text-base text-zinc-400 leading-relaxed max-w-2xl">
            {symptom.hint} — a complaint frequently reported by{" "}
            {occupation.name.toLowerCase()}, whose routines typically involve{" "}
            {occupation.context}. This page offers an educational, non-clinical
            screening and gentle lifestyle possibilities.
          </p>

          {/* Gentle possibilities */}
          <div className="mt-8 rounded-3xl border border-white/10 bg-white/[0.03] p-6">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold mb-4">
              What often helps — non-clinical possibilities
            </div>
            <ul className="space-y-3">
              {symptom.tips.map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-zinc-200 leading-relaxed">
                  <span className="w-5 h-5 rounded-full bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-emerald-400" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>

          {/* Interactive screening */}
          <div
            data-testid="ptool-screening"
            className="mt-8 rounded-3xl border border-cyan-400/20 bg-gradient-to-br from-[#0B3D91]/15 to-cyan-500/[0.06] p-6 sm:p-8"
          >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-cyan-400 font-semibold">
              <Sparkles className="w-3.5 h-3.5" />
              30-second lifestyle screening
            </div>
            <h2 className="mt-2 font-display text-2xl font-semibold">
              Your lifestyle index, right now
            </h2>
            <p className="mt-1 text-xs text-zinc-500">
              Two sliders. Instant result. Nothing leaves your device.
            </p>

            <div className="mt-6 grid sm:grid-cols-2 gap-5">
              <div className="rounded-2xl bg-black/40 border border-white/10 p-5">
                <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                  <span className="inline-flex items-center gap-1.5">
                    <BedDouble className="w-3.5 h-3.5 text-cyan-400" />
                    Sleep hours last night
                  </span>
                  <span data-testid="ptool-sleep-value" className="text-cyan-300 text-sm font-semibold normal-case tracking-normal">
                    {sleep}h
                  </span>
                </div>
                <input
                  data-testid="ptool-sleep-slider"
                  type="range"
                  min="3"
                  max="10"
                  step="0.5"
                  value={sleep}
                  onChange={(e) => setSleep(Number(e.target.value))}
                  className="w-full mt-3 accent-cyan-400"
                />
                <div className="flex justify-between text-[10px] text-zinc-600 mt-1">
                  <span>3h</span>
                  <span>10h</span>
                </div>
              </div>
              <div className="rounded-2xl bg-black/40 border border-white/10 p-5">
                <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                  <span className="inline-flex items-center gap-1.5">
                    <Coffee className="w-3.5 h-3.5 text-cyan-400" />
                    Daily caffeine intake
                  </span>
                  <span data-testid="ptool-caffeine-value" className="text-cyan-300 text-sm font-semibold normal-case tracking-normal">
                    {caffeine} {caffeine === 1 ? "cup" : "cups"}
                  </span>
                </div>
                <input
                  data-testid="ptool-caffeine-slider"
                  type="range"
                  min="0"
                  max="6"
                  step="1"
                  value={caffeine}
                  onChange={(e) => setCaffeine(Number(e.target.value))}
                  className="w-full mt-3 accent-cyan-400"
                />
                <div className="flex justify-between text-[10px] text-zinc-600 mt-1">
                  <span>0</span>
                  <span>6</span>
                </div>
              </div>
            </div>

            {/* Scorecard */}
            <div data-testid="ptool-scorecard" className="mt-6 rounded-2xl bg-black/40 border border-white/10 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <div className="relative w-24 h-24 shrink-0">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="9" />
                  <circle
                    cx="50" cy="50" r="42" fill="none"
                    stroke={band.color} strokeWidth="9" strokeLinecap="round"
                    strokeDasharray={`${(index / 100) * 264} 264`}
                    style={{ transition: "stroke-dasharray 0.8s ease, stroke 0.4s ease" }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div data-testid="ptool-index-value" className="font-display text-2xl font-semibold" style={{ color: band.color }}>
                    {index}
                  </div>
                  <div className="text-[9px] text-zinc-500 uppercase tracking-widest">index</div>
                </div>
              </div>
              <div className="flex-1">
                <div
                  data-testid="ptool-index-band"
                  className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-widest"
                  style={{ color: band.color, background: `${band.color}14`, border: `1px solid ${band.color}40` }}
                >
                  {band.label}
                </div>
                <p data-testid="ptool-scorecard-message" className="mt-3 text-sm text-zinc-300 leading-relaxed">
                  {scorecardMessage(index, symptom, occupation)}
                </p>
              </div>
            </div>

            <SmartwatchCTA variant="fix" source={`ptool-${symptom.slug}-${occupation.slug}`} />

            <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3">
              <button
                data-testid="ptool-full-assessment-btn"
                onClick={openChatbot}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full btn-glow text-white text-sm font-medium"
              >
                Take the full 16-question AI check-in
                <ArrowRight className="w-4 h-4" />
              </button>
              <span className="text-[11px] text-zinc-500">
                Free · no login · ~3 minutes
              </span>
            </div>
          </div>

          {/* Related guides — internal linking */}
          <div className="mt-10 grid sm:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold mb-3">
                <Activity className="w-3.5 h-3.5" />
                {symptom.name} — other routines
              </div>
              <div className="space-y-2">
                {relatedSymptoms.map((o) => (
                  <Link
                    key={o.slug}
                    to={toolPath(symptom, o)}
                    data-testid={`related-occ-${o.slug}`}
                    className="block px-4 py-2.5 rounded-xl bg-white/[0.02] border border-white/5 hover:border-cyan-400/30 text-xs text-zinc-300 hover:text-white transition-colors"
                  >
                    Fix {symptom.name.toLowerCase()} for {o.name.toLowerCase()}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold mb-3">
                <Activity className="w-3.5 h-3.5" />
                {occupation.name} — other complaints
              </div>
              <div className="space-y-2">
                {relatedOccupations.map((s) => (
                  <Link
                    key={s.slug}
                    to={toolPath(s, occupation)}
                    data-testid={`related-sym-${s.slug}`}
                    className="block px-4 py-2.5 rounded-xl bg-white/[0.02] border border-white/5 hover:border-cyan-400/30 text-xs text-zinc-300 hover:text-white transition-colors"
                  >
                    Fix {s.name.toLowerCase()} for {occupation.name.toLowerCase()}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <ComplianceFooter variant="provide" />
        </motion.div>
      </div>
    </div>
  );
}
